import React, { useEffect, useState } from "react";
import { Box, VStack, HStack, Text, IconButton, Button } from "@chakra-ui/react";
import { FaExclamationCircle, FaExclamationTriangle, FaCog } from "react-icons/fa";
import { FaChevronDown, FaChevronRight } from "react-icons/fa6";
import { AddComponentButton } from "./AddComponentButton";
import type { Component, SectionComponent } from "./types";
import { ComponentType, canComponentHaveChildren } from "./types";
import {
  NavigableTreeItem,
  NavigableTreeItemExpandButton,
  NavigableTreeItemGroup,
} from "./components/NavigableTree";
import { useNavigableTreeItemContext } from "./contexts/NavigableTreeItemContext";
import { useMessageBuilderContext } from "./MessageBuilderContext";
import { SlidingConfigPanel } from "./SlidingConfigPanel";

import getMessageBuilderComponentLabel from "./utils/getMessageBuilderComponentLabel";
import getMessageBuilderComponentIcon from "./utils/getMessageBuilderComponentIcon";
import { notifyInfo } from "@/utils/notifyInfo";
import { MESSAGE_BUILDER_MOBILE_BREAKPOINT } from "./constants/MessageBuilderMobileBreakpoint";

interface ComponentTreeItemProps {
  component: Component;
  depth?: number;
  scrollToComponentId?: string | null;
  componentIdsWithErrors: Set<string>;
  componentIdsWithWarnings: Set<string>;
  isAccessory?: boolean;
}

const ComponentTreeItemInner: React.FC<ComponentTreeItemProps> = ({
  component,
  depth = 0,
  scrollToComponentId,
  componentIdsWithErrors,
  componentIdsWithWarnings,
  isAccessory = false,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const { onExpanded } = useNavigableTreeItemContext();
  const { addChildComponent } = useMessageBuilderContext();
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);

  const hasChildren = component.children && component.children.length > 0;
  const hasAccessory =
    component.type === ComponentType.V2Section &&
    (component as SectionComponent).accessory !== undefined;
  const canHaveChildren = canComponentHaveChildren(component.type);
  const { isFocused, isExpanded, isSelected } = useNavigableTreeItemContext();

  const handleAddChild = (childType: ComponentType, asAccessory?: boolean) => {
    const added = addChildComponent(component.id, childType as any, asAccessory);

    if (added) {
      notifyInfo(
        `Successfully added ${getMessageBuilderComponentLabel(
          childType,
        )} component under ${getMessageBuilderComponentLabel(component.type)}`,
      );
    }
  };

  React.useEffect(() => {
    if (scrollToComponentId === component.id && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [scrollToComponentId]);

  useEffect(() => {
    onExpanded();
  }, []);

  const showMobileActions = isSelected;

  return (
    <VStack align="stretch" gap={0} position="relative" ref={ref}>
      <HStack
        position="relative"
        pl={2 + depth * 4}
        pr={2}
        py={1.5}
        cursor="pointer"
        bg={isSelected ? "brand.muted" : "transparent"}
        _hover={{ bg: isSelected ? "brand.muted" : "bg.emphasized" }}
        outline={isFocused ? "2px solid var(--app-fg)" : undefined}
        outlineOffset="-2px"
        data-tour-target={isSelected ? "selected-component" : undefined}
      >
        {canHaveChildren && (
          <NavigableTreeItemExpandButton>
            {({ onClick }) => {
              return (
                <IconButton
                  tabIndex={-1}
                  size="2xs"
                  variant="ghost"
                  aria-label={isExpanded ? "Collapse" : "Expand"}
                  onClick={onClick}
                >
                  {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                </IconButton>
              );
            }}
          </NavigableTreeItemExpandButton>
        )}
        {!canHaveChildren && <Box w={6} />}
        <Box fontSize="xs" mr={2} color="fg.muted">
          {React.createElement(getMessageBuilderComponentIcon(component.type))}
        </Box>
        <HStack flex={1} justifyContent="space-between" position="relative">
          <HStack>
            <Text fontSize="sm" color="fg">
              {isAccessory && (
                <Text as="span" color={isSelected ? "fg" : "fg.muted"} fontWeight="normal">
                  [Accessory]{" "}
                </Text>
              )}
              {getMessageBuilderComponentLabel(component.type)}
            </Text>
            {componentIdsWithErrors.has(component.id) && (
              <Box
                as="span"
                color={isSelected ? "fg" : "red.400"}
                flexShrink={0}
                aria-label="Error detected"
                title="Error detected"
                display="inline-flex"
              >
                <FaExclamationCircle />
              </Box>
            )}
            {!componentIdsWithErrors.has(component.id) &&
              componentIdsWithWarnings.has(component.id) && (
                <Box
                  as="span"
                  color={isSelected ? "fg" : "orange.400"}
                  flexShrink={0}
                  aria-label="Warning detected"
                  title="Warning detected"
                  display="inline-flex"
                >
                  <FaExclamationTriangle />
                </Box>
              )}
          </HStack>
        </HStack>{" "}
        {/* Always render the button (hidden when not selected) so Google Translate
            processes it on initial load instead of missing dynamically inserted nodes.
            Use CSS display instead of JS isDesktop to avoid flash on tree remount
            (useBreakpointValue returns the base/false value on first render). */}
        <Box
          display={{ base: "none", [MESSAGE_BUILDER_MOBILE_BREAKPOINT]: "block" }}
          position="absolute"
          right="5px"
          top="50%"
          transform="translateY(-50%)"
          hidden={!isSelected}
        >
          <AddComponentButton
            component={component}
            canHaveChildren={canHaveChildren}
            onAddChild={handleAddChild}
          />
        </Box>
      </HStack>
      {/* Mobile inline action buttons - use CSS display to avoid flash on tree remount */}
      <Box display={{ base: "block", [MESSAGE_BUILDER_MOBILE_BREAKPOINT]: "none" }}>
        {showMobileActions && (
          <HStack pl={2 + depth * 4 + 6} pr={2} py={2} gap={2} bg="bg.emphasized">
            <AddComponentButton
              component={component}
              canHaveChildren={canHaveChildren}
              onAddChild={handleAddChild}
              buttonProps={{
                size: "sm",
                variant: "solid",
              }}
            />
            <Button size="sm" variant="solid" onClick={() => setIsConfigPanelOpen(true)}>
              <FaCog />
              Configure
            </Button>
          </HStack>
        )}
        <SlidingConfigPanel
          isOpen={isConfigPanelOpen}
          onClose={() => setIsConfigPanelOpen(false)}
        />
      </Box>
      {/* Always render children (NavigableTreeItemGroup handles hiding via hidden attribute).
          This ensures child nodes exist in the DOM on initial render so Google Translate
          can process them before the async expand fires. */}
      {(hasChildren || hasAccessory) && (
        <VStack align="stretch" gap={0}>
          <NavigableTreeItemGroup>
            {component.children?.map((child) => (
              <NavigableTreeItem ariaLabel={child.name} id={child.id} key={child.id}>
                <ComponentTreeItem
                  component={child}
                  depth={depth + 1}
                  scrollToComponentId={scrollToComponentId}
                  componentIdsWithErrors={componentIdsWithErrors}
                  componentIdsWithWarnings={componentIdsWithWarnings}
                />
              </NavigableTreeItem>
            ))}
            {hasAccessory && (
              <NavigableTreeItem
                ariaLabel={`${(component as SectionComponent).accessory!.name} (Accessory)`}
                id={(component as SectionComponent).accessory!.id}
                key={`accessory-${(component as SectionComponent).accessory!.id}`}
              >
                <ComponentTreeItem
                  component={(component as SectionComponent).accessory!}
                  depth={depth + 1}
                  scrollToComponentId={scrollToComponentId}
                  componentIdsWithErrors={componentIdsWithErrors}
                  componentIdsWithWarnings={componentIdsWithWarnings}
                  isAccessory
                />
              </NavigableTreeItem>
            )}
          </NavigableTreeItemGroup>
        </VStack>
      )}
    </VStack>
  );
};

// Re-render only when something the item visually depends on changes. When a
// sibling subtree's content updates, our `component` reference stays referentially
// equal (RHF setValue only clones the path down to the modified node), so we skip.
export const ComponentTreeItem = React.memo(ComponentTreeItemInner, (prev, next) => {
  if (prev.component !== next.component) return false;
  if (prev.depth !== next.depth) return false;
  if (prev.isAccessory !== next.isAccessory) return false;
  if (prev.scrollToComponentId !== next.scrollToComponentId) return false;

  if (prev.componentIdsWithErrors !== next.componentIdsWithErrors) return false;
  if (prev.componentIdsWithWarnings !== next.componentIdsWithWarnings) return false;

  return true;
});
